import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader.js"
import Alert from '../common/Alert.js';
import contractPartyService from '../../services/contractPartyService.js';
import { UserCog } from 'lucide-react';
import { validators, inputFilters, useValidation } from '../common/Validation.js';
import ContractPartyForm from './ContractPartyForm.js';
import { useTranslation } from 'react-i18next';


const EditContractParty = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { partyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    gst_number: '',
    company_name: '',
    address: '',
    status: 'active'
  });
  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    name:         (v) => validators.required(v, 'Name') || validators.textOnly(v, 'Name'),
    contact:      (v) => validators.required(v, 'Contact') || validators.contact(v),
    email:        (v) => validators.required(v, 'Email') || validators.email(v),
    company_name: (v) => validators.required(v, 'Company name'),
    gst_number:   (v) => v ? validators.gst(v) : '',
    address:      (v) => validators.required(v, 'Address'),
  };

  useEffect(() => {
    const fetchParty = async () => {
      try {
        const response = await contractPartyService.getById(partyId);
        const party = response.data;
        setFormData({
          name: party.name,
          contact: party.contact,
          email: party.email,
          gst_number: party.gst_number || '',
          company_name: party.company_name,
          address: party.address,
          status: party.status
        });
      } catch (err) {
        Alert.error('Failed to load contract party');
        console.error(err);
        navigate('/contract-parties');
      } finally {
        setLoading(false);
      }
    };

    fetchParty();
  }, [partyId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'contact') filtered = inputFilters.digits10(value);
    else if (name === 'name') filtered = inputFilters.lettersOnly(value);
    else if (name === 'gst_number') filtered = value.toUpperCase();

    setFormData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(formData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    try {
      setSubmitting(true);
      await contractPartyService.update(partyId, formData);
      Alert.success('Contract party updated successfully!');
      navigate('/contract-parties');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to update contract party');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading contract party..." />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <ContractPartyForm
      title={t('pages.contractParty.edit')}
      icon={UserCog}
      formData={formData}
      errors={errors}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onCancel={() => navigate('/contract-parties')}
      loading={submitting}
      submitLabel="Update Contract Party"
      loadingLabel="Updating..."
    />
  );
};

export default EditContractParty;
